import 'react-tooltip/dist/react-tooltip.css';
import CloseIcon from 'svg/CloseIcon';
import InfoCircle from 'svg/InfoCircle';
import { ButtonText } from 'components/Button';
import Modal, { useModal } from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

interface ProfileItemProps {
  label: string;
  component: React.ReactNode;
  description: string;
  hasBottomBorder?: boolean;
  isOptional?: boolean;
  customClass?: string;
  invert?: boolean;
  helperText?: string;
}

const ProfileItem = ({
  label,
  component,
  description,
  hasBottomBorder = true,
  isOptional = false,
  customClass = '',
  invert = false,
  helperText,
}: ProfileItemProps) => {
  const {
    isOpen: isInfoModalOpen,
    openModal: openInfoModalOpen,
    closeModal: closeInfoModalOpen,
  } = useModal();

  return (
    <>
      <div
        className={classNames(
          'mt-8 pb-ds-xl',
          hasBottomBorder
            ? invert
              ? 'border-b border-color-border-card-darkmode'
              : 'border-b border-color-border-input-lightmode'
            : '',

          'mx-4 flex flex-col md:mx-0 md:flex-row',
          customClass,
        )}
      >
        <div
          className={'mb-6 flex flex-row xs:w-full xs:justify-between md:mb-0 md:w-1/4 md:flex-col'}
        >
          <div className="flex items-center">
            <div>
              <p
                className={classNames(
                  'typography-product-subheading',
                  invert
                    ? 'text-color-text-darkmode-secondary'
                    : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                )}
              >
                {label}
              </p>
              {helperText && (
                <p
                  className={classNames(
                    'typography-product-caption mt-2',
                    invert
                      ? 'text-color-text-darkmode-tertiary'
                      : 'text-color-text-lightmode-tertiary',
                  )}
                >
                  {helperText}
                </p>
              )}
            </div>
          </div>
          <div className="xs:inline md:hidden">
            <span
              onClick={() => {
                openInfoModalOpen();
              }}
            >
              <InfoCircle className="h-5 w-5 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
            </span>
          </div>

          {isOptional && (
            <p className="typography-product-body-highlight text-color-text-lightmode-tertiary">
              {'(Optional)'}
            </p>
          )}
        </div>
        <div className="mr-4 w-full min-w-0 md:w-1/2">{component}</div>
        <p
          className={classNames(
            'typography-product-caption mt-4 w-full xs:hidden sm:mt-0 sm:w-1/4 md:inline',
            invert
              ? 'text-color-text-darkmode-placeholder'
              : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
          )}
        >
          {description}
        </p>
      </div>
      <Modal isOpen={isInfoModalOpen} handleClose={closeInfoModalOpen}>
        <div className="mb-10 flex flex-col px-4 pt-6">
          <div className="flex flex-row justify-between">
            <p className="typography-product-heading-mobile">{label}</p>
            <ButtonText onClick={() => closeInfoModalOpen()}>
              <CloseIcon className="h-6 w-6" />
            </ButtonText>
          </div>
          <div className="min-h-32">
            <p
              className={classNames(
                'typography-product-caption mt-4 w-full sm:mt-0 sm:w-1/4',
                invert
                  ? 'text-color-text-darkmode-placeholder'
                  : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
              )}
            >
              {description}
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProfileItem;
